import { useEffect, useRef, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call } from '../api/library.js';
import { useApp } from '../state/app.js';
import { Spinner } from '../ui/common.js';

// 오늘의 식단 — 1층 로비 메뉴판 사진을 찍어 올리고, 중식·석식 메뉴를 입력(OCR 자동 입력은 추후)
interface Meal { date: string; lunch: string; dinner: string; hasPhoto: boolean; updatedAt?: string; updatedByName?: string }

const kstDate = (d = new Date()) => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(d);
const shift = (date: string, n: number) => kstDate(new Date(Date.parse(`${date}T12:00:00+09:00`) + n * 86_400_000));
const label = (date: string) => new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', month: 'long', day: 'numeric', weekday: 'short' }).format(new Date(`${date}T12:00:00+09:00`));

/** 휴대폰 사진(수 MB)을 긴 변 1600px JPEG 로 줄여 base64 로 */
async function shrink(file: File): Promise<string> {
  const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const k = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas');
  c.width = Math.round(bmp.width * k);
  c.height = Math.round(bmp.height * k);
  c.getContext('2d')!.drawImage(bmp, 0, 0, c.width, c.height);
  return c.toDataURL('image/jpeg', 0.85).split(',')[1]!;
}

function Photo({ date, version }: { date: string; version: string }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [big, setBig] = useState(false);
  useEffect(() => {
    let u: string | null = null;
    let alive = true;
    call(`/meals/photo?date=${date}&v=${encodeURIComponent(version)}`).then((r) => r.blob()).then((b) => { if (alive) { u = URL.createObjectURL(b); setUrl(u); } }, () => setUrl(null));
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [date, version]);
  if (!url) return <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>;
  return (
    <>
      <img src={url} alt={`${label(date)} 메뉴판 사진`} onClick={() => setBig(true)} style={{ width: '100%', borderRadius: 8, cursor: 'zoom-in', display: 'block' }} />
      {big && (
        <div className="dx-overlay" onClick={() => setBig(false)} style={{ cursor: 'zoom-out' }}>
          <img src={url} alt="" style={{ maxWidth: '96vw', maxHeight: '94vh', borderRadius: 8 }} />
        </div>
      )}
    </>
  );
}

export function Meals(): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  const [date, setDate] = useState(kstDate());
  const q = useQuery({ queryKey: ['meals', date], queryFn: async () => (await (await call(`/meals?date=${date}`)).json()) as Meal });
  const [edit, setEdit] = useState(false);
  const [lunch, setLunch] = useState('');
  const [dinner, setDinner] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const m = q.data;
  useEffect(() => { setEdit(false); }, [date]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['meals', date] });
  const save = useMutation({
    mutationFn: () => call('/meals', { date, lunch, dinner }),
    onSuccess: () => { flash('메뉴를 저장했습니다'); setEdit(false); refresh(); },
    onError: (e) => flash(e instanceof Error ? e.message : '저장 실패'),
  });
  const upload = useMutation({
    mutationFn: async (f: File) => call('/meals/photo', { date, contentBase64: await shrink(f) }),
    onSuccess: () => { flash('사진을 올렸습니다. 메뉴도 입력해 주세요'); refresh(); },
    onError: (e) => flash(e instanceof Error ? e.message : '사진 올리기 실패'),
  });
  const startEdit = () => { setLunch(m?.lunch ?? ''); setDinner(m?.dinner ?? ''); setEdit(true); };
  const isToday = date === kstDate();

  const menu = (title: string, text: string) => (
    <div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#6B7280', marginBottom: 6 }}>{title}</div>
      {text ? <ul style={{ margin: 0, paddingLeft: 18, fontSize: 15, lineHeight: 1.75, color: '#1F2937' }}>{text.split('\n').filter((l) => l.trim()).map((l, i) => <li key={i}>{l}</li>)}</ul>
        : <div style={{ fontSize: 13, color: '#9CA3AF' }}>아직 입력되지 않았습니다</div>}
    </div>
  );

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gap: 14, animation: 'dxfade .3s ease' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button className="dx-btn dx-btn-ghost dx-btn-sm" onClick={() => setDate(shift(date, -1))} aria-label="전날">‹</button>
        <div style={{ fontSize: 15, fontWeight: 700, minWidth: 120, textAlign: 'center' }}>{label(date)}{isToday && <span style={{ marginLeft: 6, fontSize: 12, color: '#2563EB' }}>오늘</span>}</div>
        <button className="dx-btn dx-btn-ghost dx-btn-sm" onClick={() => setDate(shift(date, 1))} aria-label="다음날">›</button>
        {!isToday && <button className="dx-btn dx-btn-soft dx-btn-sm" onClick={() => setDate(kstDate())}>오늘</button>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />
          <button className="dx-btn dx-btn-primary dx-btn-sm" disabled={upload.isPending} onClick={() => fileRef.current?.click()}>{upload.isPending ? '올리는 중…' : m?.hasPhoto ? '사진 다시 올리기' : '메뉴판 사진 올리기'}</button>
          {!edit && <button className="dx-btn dx-btn-ghost dx-btn-sm" onClick={startEdit}>메뉴 입력</button>}
        </span>
      </div>

      {q.isLoading || !m ? <div style={{ padding: 60, textAlign: 'center' }}><Spinner /></div> : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
          <section className="dx-card" style={{ padding: 18, flex: '1 1 320px', display: 'grid', gap: 18 }}>
            {edit ? (
              <>
                <label style={{ display: 'grid', gap: 6, fontSize: 12.5, fontWeight: 700, color: '#6B7280' }}>중식 (한 줄에 한 메뉴)
                  <textarea className="dx-textarea" rows={7} maxLength={1000} value={lunch} onChange={(e) => setLunch(e.target.value)} placeholder={'예)\n잡곡밥\n김치찌개\n제육볶음'} />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12.5, fontWeight: 700, color: '#6B7280' }}>석식 (없으면 비워 두세요)
                  <textarea className="dx-textarea" rows={4} maxLength={1000} value={dinner} onChange={(e) => setDinner(e.target.value)} />
                </label>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button className="dx-btn dx-btn-ghost dx-btn-sm" onClick={() => setEdit(false)}>취소</button>
                  <button className="dx-btn dx-btn-primary dx-btn-sm" disabled={save.isPending} onClick={() => save.mutate()}>저장</button>
                </div>
              </>
            ) : (
              <>
                {menu('중식', m.lunch)}
                {m.dinner && menu('석식', m.dinner)}
                {m.updatedByName && <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{m.updatedByName} 등록 · {new Date(m.updatedAt!).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</div>}
              </>
            )}
          </section>
          <section className="dx-card" style={{ padding: 12, flex: '1 1 320px' }}>
            {m.hasPhoto ? <Photo date={date} version={m.updatedAt ?? ''} />
              : <div style={{ padding: '46px 16px', textAlign: 'center', color: '#9CA3AF', fontSize: 13, lineHeight: 1.7 }}>1층 로비 메뉴판 사진이 아직 없습니다.<br />사진을 찍어 올려 주세요 📷</div>}
          </section>
        </div>
      )}
      <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>누구나 사진을 올리고 메뉴를 입력·수정할 수 있습니다. 사진 속 메뉴 자동 인식(OCR)은 추후 지원 예정입니다.</div>
    </div>
  );
}
